import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  NativeSelect,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  addBulkMatchedWholegoods,
  addDmsWhoolegoods,
  addMatchedProducts,
  removeAllDmsWhoolegoods,
  useGetDMSManufacturersBySettingsIdQuery,
  useGetDmsWholegoodsV2Mutation,
  useGetMatchingProductsV2Mutation,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import PartGrid from "./Grids/PartGrid";
import {
  PlatformFeature,
  LocationPart,
  LocationWholegood,
  MatchedProduct,
  SetupType,
} from "../../../models/features/Integration/DmsSetup";
import { useGetDmsPartsMutation } from "../../../redux/reducer/features/ShoppingPortal/ShoppingPortalSlice";
import { DmsWhlegoodRequestDto } from "../../../models/features/ShoppingPortal/DmsPart";
import { AppFilter } from "../../../models/app/AppModels";
import ServerAppGrid from "../../app/ServerAppGrid";
import {
  GridColDef,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { AppGridId } from "../../../models/app/AppGridModels";
import { GetAppGridColumns } from "../../../services/utilities/utility";
import { DmsManufacturer } from "../../../models/features/ShoppingPortal/DmsManufacturer";
import TruncatedCellWithTooltip from "../../common/TruncatedCellWithTooltip";
import { useLocation } from "react-router";
import { hasPlatformFeatureAccess } from "../../../services/featurePermissions/featureMatrix";
import {
  SubscriptionFeatureName,
  useHasSubscriptionFeature,
} from "../../../services/featurePermissions/subscriptionFeatures";

interface DmsProductsProps {
  handleNext: any;
  handleBack: any;
}

const DmsProducts: React.FunctionComponent<DmsProductsProps> = ({
  handleBack,
  handleNext,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const hasCatalogMatching = useHasSubscriptionFeature(
    SubscriptionFeatureName.CatalogMatching,
  );
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.Create_Setup_DMS_Products,
    ),
  );
  const { dmsSetup } = useSelector((state: AppState) => state.authReducer);

  const locationIds = useSelector(
    (state: AppState) => state.dmsReducer.locationIds,
  );
  const setupType = useSelector(
    (state: AppState) => state.dmsReducer.setupType,
  );
  const selectedProducts = useSelector(
    (state: AppState) => state.dmsReducer.dmsWholeGoods,
  );
  const [paging, setPaging] = React.useState<AppFilter>({
    pageIndex: 0,
    pageSize: 100,
    totalRecords: 0,
    items: [],
    quickFilterValues: [],
  });
  const { dmsWholeGoods, dmsManufacturers } = useSelector(
    (state: AppState) => state.dmsReducer,
  );
  const [dmsWhoolgoodsMutation, dmsWhoolgoodsMutationResult] =
    useGetDmsWholegoodsV2Mutation();
  const [getMatchingWhoolegoods, matchingWhoolegoodsMutationResult] =
    useGetMatchingProductsV2Mutation();
  const { data, refetch } = useGetDMSManufacturersBySettingsIdQuery(
    {
      dealerSettingsId: dmsSetup?.dealerSettings?.id ?? "",
    },
    { skip: !dmsSetup?.id },
  );

  const [wholegoods, setWholegoods] = React.useState<LocationWholegood[]>([]);
  const [productsCount, setProductsCount] = React.useState<number>(0);
  const [withQtyOnly, setWithQtyOnly] = React.useState(true);
  const [selectedManufacturers, setSelectedManufacturers] = React.useState<
    DmsManufacturer[]
  >([]);

  React.useEffect(() => {
    if (location.pathname === "/addSite") {
      if (data && data.status === 200) {
        setSelectedManufacturers(data?.entityList ?? []);
      }
    } else {
      setSelectedManufacturers(dmsManufacturers);
    }
  }, [dmsManufacturers, data]);

  React.useEffect(() => {
    fetchDMSWholegoodsData();
  }, [paging, withQtyOnly, selectedManufacturers]);

  const fetchDMSWholegoodsData = () => {
    let dmsManufacturersList: string[] = [];
    if (selectedManufacturers.length > 0) {
      dmsManufacturersList = selectedManufacturers.map((n) => n.manufacturerId);
    } else {
      dmsManufacturersList = dmsManufacturers.map((n) => n.manufacturerId);
    }
    let request: DmsWhlegoodRequestDto = {
      dealerIds: locationIds,
      search: paging.quickFilterValues.join(" "),
      pageNumber: paging.pageIndex,
      pageSize: paging.pageSize,
      searchModels: [],
      stockingOnly: false,
      wildCardSearch: true,
      excludePricing: true,
      withQtyOnly: withQtyOnly,
      excludeInventoryModelId: [],
      searchManufacturerIds: dmsManufacturersList,
    };
    dmsWhoolgoodsMutation({ data: request });
  };

  React.useEffect(() => {
    if (
      dmsWhoolgoodsMutationResult.data &&
      dmsWhoolgoodsMutationResult.isSuccess
    ) {
      let totalRecords = dmsWhoolgoodsMutationResult.data.totalRecords;
      setWholegoods(dmsWhoolgoodsMutationResult.data.entityList);
      setProductsCount(totalRecords);
    }
  }, [dmsWhoolgoodsMutationResult]);

  React.useEffect(() => {
    dispatch(removeAllDmsWhoolegoods());
  }, [withQtyOnly, selectedManufacturers]);

  const handleItemSelect = (items: any[]) => {
    if (items.length === 0) {
      dispatch(addBulkMatchedWholegoods([]));
    }
    const updatedSelectedProducts =
      selectedProducts.filter((sp) => items.includes(sp.inventoryModelId)) ??
      [];

    wholegoods.forEach((item) => {
      if (
        items.includes(item.inventoryModelId) &&
        !selectedProducts.some(
          (sp) => sp.inventoryModelId === item.inventoryModelId,
        )
      ) {
        updatedSelectedProducts.push(item);
      }
    });
    dispatch(
      addDmsWhoolegoods({
        whoolegoods: updatedSelectedProducts,
      }),
    );
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWithQtyOnly(e.target.checked);
  };

  const onManufacturerChange = (value: DmsManufacturer[]) => {
    setSelectedManufacturers(value);
  };

  const handleNextStep = () => {
    // For HiImpact, skip catalog matching and mark all products as not matched
    if (
      hasCatalogMatching &&
      hasPlatformFeatureAccess(
        setupType,
        PlatformFeature.ProductsMatchingWithCatalog,
      )
    ) {
      // For other setup types, call the matching API
      getMatchingWhoolegoods({
        wholegoods: dmsWholeGoods ?? [],
        parts: [],
        dmsSetupId: "",
        dmsManufacturers: dmsManufacturers,
      }).then((res: any) => {
        if (res.data && res.data.entityList) {
          var matchAndMissMatchArray: MatchedProduct[] = [];
          res.data.entityList.forEach((n: any) => {
            matchAndMissMatchArray.push(n);
          });
          dispatch(addBulkMatchedWholegoods([...matchAndMissMatchArray]));
          handleNext();
        }
      });
    } else {
      const notMatchedProducts: MatchedProduct[] = dmsWholeGoods.map(
        (product: LocationWholegood) => ({
          dmsProduct: product,
          catalogProduct: undefined,
          isManuallyMatched: false,
          dmsPart: undefined,
        }),
      );
      dispatch(addBulkMatchedWholegoods(notMatchedProducts));
      handleNext();
      return;
    }
  };

  function CustomFilterInputValue(props: GridFilterInputValueProps) {
    const { item, applyValue } = props;

    const ratingRef: React.Ref<any> = React.useRef(null);

    const handleDropdownFilterChange = (event: any) => {
      applyValue({
        ...item,
        value: event.target.value !== "all" ? event.target.value : undefined,
      });
    };
    return (
      <FormControl fullWidth>
        <InputLabel variant="standard" htmlFor="uncontrolled-native">
          Manufacturer
        </InputLabel>
        <NativeSelect
          ref={ratingRef}
          value={item.value ? item.value : "all"}
          defaultValue="all"
          onChange={handleDropdownFilterChange}
        >
          <option value={"all"}>All</option>
          {dmsManufacturers.map((n) => (
            <option value={n.manufacturerId}>{n.name}</option>
          ))}
        </NativeSelect>
      </FormControl>
    );
  }
  const customFilterOperators: GridFilterOperator[] = [
    {
      label: "Equals",
      value: "equals",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (
          !filterItem.columnField ||
          !filterItem.value ||
          !filterItem.operatorValue
        ) {
          return null;
        }

        return (params): boolean => {
          return params.value == filterItem.value;
        };
      },
      InputComponent: CustomFilterInputValue,
      InputComponentProps: { type: "number" },
    },
  ];
  const columns: GridColDef[] = [
    {
      field: "manufacturer", //this filed name is being used in above mfr filter
      headerName: "Manufacturer",
      flex: 1,
      filterOperators: customFilterOperators,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.mfgName} />;
      },
    },
    {
      field: "model",
      headerName: "MPN",
      flex: 1,
      filterable: false,
      renderHeader: () => (
        <Tooltip title="Manufacturer Product Number">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            MPN
          </Typography>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.model} />;
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.description} />;
      },
    },
    {
      field: "productLine",
      headerName: "Category",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.productLine} />;
      },
    },
    {
      field: "subProduct",
      headerName: "Sub-Category",
      flex: 1,
      filterable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        return <TruncatedCellWithTooltip value={params.row.subProduct} />;
      },
    },
    {
      field: "cost",
      headerName: "Cost ($)",
      headerAlign: "right",
      flex: 1,
      align: "right",
      type: "number",
      filterable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.cost.toFixed(2),
    },
    {
      field: "price",
      headerName: "MSRP ($)",
      headerAlign: "right",
      flex: 1,
      type: "number",
      filterable: false,
      align: "right",
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.price.toFixed(2),
    },
    {
      field: "count",
      headerName: "In Store",
      align: "right",
      headerAlign: "right",
      type: "number",
      flex: 1,
      filterable: false,
      valueGetter: (params: GridValueGetterParams<LocationWholegood>) => {
        if (params.row?.locationCount) {
          return params.row?.locationCount?.reduce(
            (partial: number, value: any) => partial + value.count,
            0,
          );
        } else {
          return 0;
        }
      },
    },
  ];
  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        p={0.5}
      >
        <Alert severity="info">
          Please Select the Products You Want To Sync To Website. Only Selected
          Products will get match.
        </Alert>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button size="small" variant="outlined" onClick={handleBack}>
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleNextStep}
            disabled={
              dmsWhoolgoodsMutationResult.isLoading ||
              matchingWhoolegoodsMutationResult.isLoading ||
              selectedProducts.length === 0
            }
            endIcon={
              matchingWhoolegoodsMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <></>
              )
            }
          >
            Next
          </Button>
        </Box>
      </Box>
      <ServerAppGrid
        gridId={AppGridId.Create_Setup_DMS_Products}
        getRowId={(row) => row.inventoryModelId}
        data={wholegoods.map((item: any, index: any) => ({
          ...item,
          id: index,
          subProduct: item.subProduct ?? "",
          productLine: item.productLine ?? "",
          description: item.description ?? "",
        }))}
        columns={GetAppGridColumns(columns, gridSetting?.columnVisibilityModel)}
        isLoading={dmsWhoolgoodsMutationResult.isLoading}
        paging={paging}
        setPaging={setPaging}
        totalRecords={productsCount}
        selectedRows={selectedProducts.map((n) => n.inventoryModelId)}
        onSelect={(newSelection: any) => {
          handleItemSelect(newSelection);
        }}
        hideColumnFilter={false}
        height={580}
        hidePageSize={true}
        customToolbar={true}
        switchLabel="Has Quantity"
        switchChecked={withQtyOnly}
        onSwitchChange={handleSwitchChange}
        manufacturerOptions={
          location.pathname === "/addSite"
            ? (data?.entityList ?? [])
            : dmsManufacturers
        }
        selectedManufacturers={selectedManufacturers}
        onManufacturerChange={onManufacturerChange}
      />
    </>
  );
};

export default DmsProducts;
